import { useCallback, useEffect, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'
import type { z } from 'zod'
import { plural, t } from '@lingui/core/macro'
import { useMutation } from '@tanstack/react-query'

import type { FileTransferParams } from '@jotta/grpc-js-client/fileService'
import { createFolder } from '@jotta/grpc-js-client/fileService'
import {
  CopyResponse,
  MoveResponse,
} from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import { addBackgroundJob } from '@jotta/hooks'
import { queryClient } from '@jotta/query'
import type { AppError } from '@jotta/types/AppError'
import { handleUnknownError } from '@jotta/types/AppError'
import {
  isCopyErrorMessage,
  isMoveErrorMessage,
} from '@jotta/types/decodeErrorMessage'
import { isGrpcApiError } from '@jotta/types/GrpcApiError'
import { getToastContent } from '@jotta/ui/backgroundJobToasts'
import { Button } from '@jotta/ui/Button'
import { RadixDialog } from '@jotta/ui/RadixDialog'
import type { RadixModalProps } from '@jotta/ui/RadixModal'
import { RadixModal } from '@jotta/ui/RadixModal'
import SvgX from '@jotta/ui/SvgX'
import { showToast } from '@jotta/ui/ToastProvider'
import { caseInsensitiveIncludes } from '@jotta/utils/array'
import { createPath, popPath } from '@jotta/utils/pathUtils'
import { createEnum } from '@jotta/utils/schema'

import { FileTree, usePathItemFolder } from '../../FileTree/FileTree'
import type { FileActionStore } from '../FileActionStore'

const debug = Debug('jotta:files:FileTransferDialog')

function isMoveError(error: MoveResponse.MoveError) {
  return (e: unknown) =>
    isGrpcApiError(e) &&
    isMoveErrorMessage(e.decodedError) &&
    e.decodedError.toObject().moveError === error
}

function isCopyError(error: CopyResponse.CopyError) {
  return (e: unknown) =>
    isGrpcApiError(e) &&
    isCopyErrorMessage(e.decodedError) &&
    e.decodedError.toObject().copyError === error
}

function isBetweenUsersError(e: unknown) {
  return (
    isMoveError(MoveResponse.MoveError.MOVE_BETWEEN_USERS)(e) ||
    isCopyError(CopyResponse.CopyError.COPY_BETWEEN_USERS)(e)
  )
}

export type FileTransferType = 'COPY' | 'MOVE'

const labels: Record<
  FileTransferType,
  {
    button: string
    verb: string
    title: string
    smallTitle: string
  }
> = {
  get COPY() {
    return {
      button: t`Copy to folder`,
      verb: t`Copying`,
      title: t`Select the folder you want to copy the files to.`,
      smallTitle: t`Copy`,
    }
  },
  get MOVE() {
    return {
      button: t`Move to folder`,
      verb: t`Moving`,
      title: t`Select the folder you want to move the files to.`,
      smallTitle: t`Move`,
    }
  },
}

export const { isLoadingState, loadingStateSchema, loadingStates } = createEnum(
  'loadingState',
  ['ready', 'navigating', 'creating-folder', 'transferring'],
)
export type LoadingState = z.infer<typeof loadingStateSchema>

export type FileTransferDialogProps = RadixModalProps &
  Omit<FileTransferParams, 'targetDirectoryPath'> & {
    error: AppError | null
    clearError: (open?: boolean) => void
    transferFn: FileActionStore['transferItems']
    onFolderCreated?: (path: string, name: string) => void
  }

export const FileTransferDialog = observer<FileTransferDialogProps>(
  function FileTransferDialog({
    error: legacyError,
    clearError,
    transferType,
    filePaths,
    onFileTransferred,
    transferFn,
    onFolderCreated,
    ...props
  }) {
    const sourceDirectoryPaths = filePaths.map(v =>
      popPath(v).toLocaleLowerCase(),
    )
    const [targetDirectoryPath, setSelectedFolder] = useState<string>('')
    const [createFolderPath, setCreateFolderPath] = useState('')
    const [alerts, setAlerts] = useState<string[]>([])

    const useFileTransferMutation = useMutation({
      mutationFn: transferFn,
      throwOnError: false,
      onMutate: variables =>
        showToast({
          ...getToastContent(variables.transferType, 'IN_PROGRESS'),
          id: nanoid(),
        }),
      onSettled(data, error, variables, toastId) {
        if (error) {
          reportError(error)
        }
        data?.backgroundJobIds?.map(id => {
          addBackgroundJob(variables.transferType, id, toastId)
        })
        const status = data?.completedSuccessfully
          ? 'SUCCESS'
          : data?.backgroundJobIds?.length
            ? 'IN_PROGRESS'
            : 'FAILED'
        showToast({
          ...getToastContent(variables.transferType, status),
          id: toastId,
        })
      },
    })
    const { isSuccess, error } = useFileTransferMutation

    const showAlert = (newAlert: string) => {
      setSelectedFolder('')
      setAlerts(prevAlerts => {
        if (!prevAlerts.includes(newAlert)) {
          return [...prevAlerts, newAlert]
        }
        return prevAlerts
      })
    }
    useEffect(() => {
      if (!error) {
        return
      }

      const errors = Array.isArray(error) ? error : [error]
      const isShareOntoShare = isMoveError(
        MoveResponse.MoveError.MOVE_SHARE_ONTO_SHARE,
      )

      if (errors.some(isBetweenUsersError)) {
        showAlert(t`Cannot move/copy between users`)
      }
      if (errors.some(isShareOntoShare)) {
        showAlert(t`You can't move a shared folder into another shared folder`)
      }
      if (
        errors.filter(e => !isBetweenUsersError(e) && !isShareOntoShare).length
      ) {
        showAlert(t`Something went wrong`)
      }

      useFileTransferMutation.reset()
    }, [error, props, useFileTransferMutation, alerts])

    useEffect(() => {
      if (!error && isSuccess && props.onClose) {
        props.onClose(false)
      }
    }, [error, isSuccess, props])

    const createFolderMutation = useMutation({
      mutationFn: async ({
        targetPath,
        newFolderName,
      }: {
        targetPath: string
        newFolderName: string
      }) => {
        if (targetPath && newFolderName) {
          await createFolder({
            path: createPath(targetPath, newFolderName),
          })
        }
      },
      onSuccess: (data, { targetPath, newFolderName }) => {
        queryClient
          .invalidateQueries({
            queryKey: usePathItemFolder.getKey({
              path: targetPath,
            }),
          })
          .then(() => {
            setSelectedFolder(`${targetPath}/${newFolderName}`)
          })
        if (onFolderCreated) {
          onFolderCreated(targetPath, newFolderName)
        }
      },
      onError(err) {
        const error = handleUnknownError(err)
        return error
      },
      onSettled: () => setCreateFolderPath(''),
    })
    const createFolderCallback = useCallback(
      (targetPath: string, newFolderName: string) => {
        createFolderMutation.mutate({ targetPath, newFolderName })
      },
      [createFolderMutation],
    )
    const isSameFolder = caseInsensitiveIncludes(
      sourceDirectoryPaths,
      targetDirectoryPath,
    )
    const count = filePaths.length

    const cancelCreateFolder = useCallback(() => {
      setCreateFolderPath('')
    }, [])

    return (
      <>
        <RadixModal {...props}>
          <RadixDialog
            data-testid="FileTransferDialog"
            title={labels[transferType].title}
            smallTitle={
              count === 1
                ? filePaths[0]
                : plural(count, {
                    one: '# file',
                    other: '# files',
                  })
            }
            onClose={props.onClose}
            buttons={[
              {
                action: () => setCreateFolderPath(targetDirectoryPath),
                label: t`Create new folder`,
                type: 'outline',
                disabled: !targetDirectoryPath,
              },
              {
                action: 'close',
                label: t`Cancel`,
                type: 'outline',
              },
              {
                action: () =>
                  useFileTransferMutation
                    .mutateAsync({
                      transferType,
                      filePaths,
                      targetDirectoryPath,
                    })
                    .catch(() => {}),
                loading: useFileTransferMutation.isPending,
                label: labels[transferType].button,
                disabled: !targetDirectoryPath || isSameFolder,
                testId: 'ok',
                type: 'primary',
              },
            ]}
          >
            {alerts.map(alert => (
              <div
                key={alert}
                className="alert alert-danger fixed z-50 m-2 flex items-center justify-between"
              >
                {alert}
                <Button
                  className="btn btn-ghost size-5 p-0 hover:bg-transparent"
                  icon={SvgX}
                  onClick={() => setAlerts([])}
                  aria-label={t`Close alert`}
                />
              </div>
            ))}
            <p>From folder: {[...new Set(sourceDirectoryPaths)].join(', ')}</p>
            <p>To folder: {targetDirectoryPath}</p>
            <FileTree
              onSelectFolder={pathName => {
                setAlerts([])
                setSelectedFolder(pathName)
              }}
              selectedPath={targetDirectoryPath}
              path="/archive"
              createFolderPath={createFolderPath}
              onCancelCreateFolder={cancelCreateFolder}
              onCreateFolder={createFolderCallback}
            />
            <FileTree
              onSelectFolder={pathName => {
                setAlerts([])
                setSelectedFolder(pathName)
              }}
              selectedPath={targetDirectoryPath}
              initiallyExpanded
              path="/sync"
              createFolderPath={createFolderPath}
              onCancelCreateFolder={cancelCreateFolder}
              onCreateFolder={createFolderCallback}
            />
          </RadixDialog>
        </RadixModal>
      </>
    )
  },
)
